// src/components/EPLRadioGroup.jsx
// 🎯 Componente de Radio Buttons para sistema EPL
// Proporciona affordance clara para selección de opciones pedagógicas

import React, { useState } from 'react';
import { Check, ChevronRight, BookOpen, Lightbulb, Save, ArrowRight, Eye } from 'lucide-react';

// Mapeo de iconos para opciones
const iconMap = {
  'arrow-right': ArrowRight,
  'check': Check,
  'chevron-right': ChevronRight,
  'book-open': BookOpen,
  'book': BookOpen,
  'lightbulb': Lightbulb,
  'save': Save,
  'eye': Eye
};

/**
 * Radio Group específico para EPL con diseño pedagógico
 * @param {Array} opciones - Lista de opciones con etiqueta, valor, icono
 * @param {string} selectedValue - Valor actualmente seleccionado
 * @param {Function} onSelectionChange - Callback cuando cambia la selección
 * @param {string} groupName - Nombre del grupo (único por instancia)
 */
const EPLRadioGroup = ({ 
  opciones = [], 
  selectedValue, 
  onSelectionChange, 
  groupName = 'epl-options' 
}) => {
  const [focusedIndex, setFocusedIndex] = useState(0);

  const handleRadioChange = (option) => {
    onSelectionChange?.(option);
  };

  const handleKeyDown = (event, index) => {
    switch (event.key) {
      case 'ArrowDown':
      case 'ArrowRight':
        event.preventDefault();
        const nextIndex = (index + 1) % opciones.length;
        setFocusedIndex(nextIndex);
        document.getElementById(`${groupName}-${nextIndex}`)?.focus();
        break;
      
      case 'ArrowUp':
      case 'ArrowLeft':
        event.preventDefault();
        const prevIndex = index === 0 ? opciones.length - 1 : index - 1;
        setFocusedIndex(prevIndex);
        document.getElementById(`${groupName}-${prevIndex}`)?.focus();
        break;
      
      case 'Enter':
      case ' ':
        event.preventDefault();
        handleRadioChange(opciones[index]);
        break;
    }
  };

  if (!opciones || opciones.length === 0) {
    return null;
  }

  return (
    <div className="space-y-3" role="radiogroup" aria-label="Opciones de progreso">
      {opciones.map((opcion, index) => {
        const IconComponent = iconMap[opcion.icono] || ChevronRight;
        const isSelected = selectedValue === opcion.valor;
        const isValidForAdvance = opcion.valor === 'continuar' || opcion.valor === 'go_define' || opcion.valor === 'save_hypothesis';

        return (
          <label
            key={`${opcion.valor}-${index}`}
            className={`
              flex items-center p-4 rounded-lg border-2 cursor-pointer
              transition-all duration-200 group
              ${isSelected 
                ? isValidForAdvance
                  ? 'border-blue-500 bg-blue-50 shadow-md' // Seleccionado y válido
                  : 'border-orange-400 bg-orange-50 shadow-md' // Seleccionado pero ejemplo
                : 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm'
              }
              ${isSelected ? 'ring-2 ring-blue-200' : ''}
              focus-within:ring-2 focus-within:ring-blue-200 focus-within:border-blue-500
            `}
          >
            {/* Radio Input */}
            <input
              type="radio"
              id={`${groupName}-${index}`}
              name={groupName}
              value={opcion.valor}
              checked={isSelected}
              onChange={() => handleRadioChange(opcion)}
              onKeyDown={(e) => handleKeyDown(e, index)}
              className="sr-only"
              aria-describedby={`${groupName}-${index}-desc`}
            />

            {/* Custom Radio Indicator */}
            <div className={`
              flex-shrink-0 w-5 h-5 rounded-full border-2 mr-3
              flex items-center justify-center
              transition-all duration-200
              ${isSelected 
                ? isValidForAdvance
                  ? 'border-blue-500 bg-blue-500' 
                  : 'border-orange-400 bg-orange-400'
                : 'border-gray-300 group-hover:border-gray-400'
              }
            `}>
              {isSelected && (
                <div className="w-2 h-2 rounded-full bg-white"></div>
              )}
            </div>

            {/* Icon */}
            <div className={`
              flex-shrink-0 mr-3 transition-colors duration-200
              ${isSelected 
                ? isValidForAdvance 
                  ? 'text-blue-600' 
                  : 'text-orange-600'
                : 'text-gray-400 group-hover:text-gray-600'
              }
            `}>
              <IconComponent className="w-5 h-5" />
            </div>

            {/* Label Content */}
            <div className="flex-1">
              <div className={`
                font-medium transition-colors duration-200
                ${isSelected 
                  ? isValidForAdvance 
                    ? 'text-blue-900' 
                    : 'text-orange-900'
                  : 'text-gray-700 group-hover:text-gray-900'
                }
              `}>
                {opcion.etiqueta}
              </div>
              
              {/* Descripción contextual */}
              <div 
                id={`${groupName}-${index}-desc`}
                className={`
                  text-sm mt-1 transition-colors duration-200
                  ${isSelected 
                    ? isValidForAdvance 
                      ? 'text-blue-600' 
                      : 'text-orange-600'
                    : 'text-gray-500'
                  }
                `}
              >
                {isValidForAdvance 
                  ? 'Selecciona esta opción para continuar al siguiente paso'
                  : 'Ver contenido de apoyo (no avanza el progreso)'
                }
              </div>
            </div>

            {/* Visual Indicator for Advanced Options */}
            {isSelected && isValidForAdvance && (
              <div className="flex-shrink-0 ml-2">
                <Check className="w-4 h-4 text-blue-600" />
              </div>
            )}
          </label>
        );
      })}

      {/* Helper Text */}
      <div className="text-xs text-gray-500 mt-3 pl-8">
        <div className="flex items-center space-x-4">
          <div className="flex items-center">
            <div className="w-3 h-3 rounded-full bg-blue-500 mr-2"></div>
            <span>Avanza progreso</span>
          </div>
          <div className="flex items-center">
            <div className="w-3 h-3 rounded-full bg-orange-400 mr-2"></div>
            <span>Contenido de apoyo</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EPLRadioGroup;